import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, OnInit, OnChanges, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { Observable } from 'rxjs';
import { TypeaheadMatch } from 'ngx-bootstrap/typeahead';
import { StatusCode, SelectionType } from '../../_enums/Common';
import { ListingParams } from '../../_models/common/listingParams';
import { CommonService } from '@services/app.common.service';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { AutoCompleteModalComponent } from '../app-AutoCompleteModal/app-AutoCompleteModal.component';
import { AutoCompleteConfig } from '../../_models/common/AutoCompleteConfig';

@Component({
    selector: 'app-autocomplete',
    templateUrl: './app-AutoComplete.component.html',
    styleUrls: ['./app-AutoComplete.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})

export class AutoCompleteComponent implements OnInit, OnChanges {
  private _valueModel: any;

  @Input('valueModel')
  get valueModel() {
    return this._valueModel;
  }
  set valueModel(val) {
    this._valueModel = val;
    this.valueModelChange.emit(this._valueModel);
  }

  @Input() autoCompleteConfig = {} as AutoCompleteConfig;

  @Output() valueModelChange = new EventEmitter();
  @Output() bySelect = new EventEmitter();
  @Output() searchClick = new EventEmitter();

  userInput: string = "";
  isAsync: boolean = false;
  typeaheadLoading: boolean=false;
  dataSourceArray: any;
  typeaheadDataSource: any = [];
  showClearButton = false;
  showSearchButton = false;
  optionTextField = "";
  typeaheadOptionsInScrollableView = 5;
  typeaheadOptionsLimit = 1000;
  modalRef = {} as BsModalRef;

  @ViewChild('input', { static: true }) inputRef?: ElementRef;


  statusCode = StatusCode;
  listingParams = {} as ListingParams;


  constructor(private common: CommonService, private modalService: BsModalService) {
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['valueModel']) {
      this.syncUserInputFromValueModel(changes['valueModel'].currentValue);
    }
  }

  ngOnInit() {
    this.listingParams = this.common.initListingParams();
    this.autoCompleteConfig.label = this.autoCompleteConfig.label ? this.autoCompleteConfig.label : "";
    this.autoCompleteConfig.nameAttribute = this.autoCompleteConfig.nameAttribute ? this.autoCompleteConfig.nameAttribute : "";
    this.autoCompleteConfig.dataSourceUrl = this.autoCompleteConfig.dataSourceUrl ? this.autoCompleteConfig.dataSourceUrl : "";
    this.autoCompleteConfig.optionValueField = this.autoCompleteConfig.optionValueField ? this.autoCompleteConfig.optionValueField : 'id';
    this.autoCompleteConfig.placeholder = this.autoCompleteConfig.placeholder ? this.autoCompleteConfig.placeholder : "";
    this.autoCompleteConfig.required = this.autoCompleteConfig.required ? this.autoCompleteConfig.required : false;
    this.autoCompleteConfig.showSearchIcon = this.autoCompleteConfig.showSearchIcon ? this.autoCompleteConfig.showSearchIcon : false;
    this.autoCompleteConfig.allowCustomInput = this.autoCompleteConfig.allowCustomInput ? this.autoCompleteConfig.allowCustomInput : false;
    this.autoCompleteConfig.textOnly = this.autoCompleteConfig.textOnly ? this.autoCompleteConfig.textOnly : false;
    this.optionTextField = this.autoCompleteConfig.optionTextField ?? "text";
    this.autoCompleteConfig.optionTextField = this.optionTextField;

    if (!this.autoCompleteConfig.textOnly && this.autoCompleteConfig.dataSourceUrl) {
      this.isAsync = true;

      this.autoCompleteConfig.dataSource = Observable.create((observer: any) => {
        this.listingParams.textToSearch = this.userInput;
        this.listingParams.systemPageId = this.autoCompleteConfig.systemPageId??-1;

        this.common.PostData(this.autoCompleteConfig.dataSourceUrl??"", this.listingParams, false)
          .then(result => {
            if (result.status == this.statusCode.OK) {
              this.dataSourceArray = result.resultData.listData;
              observer.next(result.resultData.listData);
              this.syncUserInputFromValueModel();
            } else {
              this.dataSourceArray = null;
              this.typeaheadLoading = false;
            }
          })
          .catch((err) => {
            this.dataSourceArray = null;
            this.typeaheadLoading = false;
          });
      });
      this.typeaheadDataSource = this.autoCompleteConfig.dataSource;
    }

    if (!this.autoCompleteConfig.textOnly && this.autoCompleteConfig.dataSource) {
      if (Array.isArray(this.autoCompleteConfig.dataSource)) {
        this.dataSourceArray = this.autoCompleteConfig.dataSource;
        this.typeaheadDataSource = this.autoCompleteConfig.dataSource;
        this.syncUserInputFromValueModel();
      }
    } else if (!this.autoCompleteConfig.textOnly) {
      this.autoCompleteConfig.dataSource = [];
      this.typeaheadDataSource = [];
      console.error(`No dataSourceUrl/dataSource array provided for ${this.autoCompleteConfig.label} autocomplete. So, it will not work.`);
    }
    this.updateDisplayState();
  }

  changeTypeaheadLoading(e: boolean): void {
    this.typeaheadLoading = e;
  }

  typeaheadOnSelect(eventArgs: TypeaheadMatch): void {
    if (eventArgs.item) {
      this.valueModel = eventArgs.item;
      this.UpdateFormControl();
    }
    this.updateDisplayState();
    this.bySelect.emit(eventArgs);
  }

  onUserInputModelChange() {
    if (!this.autoCompleteConfig?.allowCustomInput) {
      return;
    }

    const trimmed = (this.userInput ?? "").trim();
    this.valueModel = trimmed.length ? trimmed : null;
    this.UpdateFormControl();
    this.updateDisplayState();
  }

  onChange() {
    let matchingItem = null;
    if (!this.autoCompleteConfig.textOnly && this.dataSourceArray && Array.isArray(this.dataSourceArray)) {
      const dataArray = this.dataSourceArray as Array<any>;
      const normalizedInput = this.userInput.trim().toLowerCase();
      matchingItem = dataArray.find(a => String(a?.[this.optionTextField] ?? "").trim().toLowerCase() == normalizedInput);
    }

    if (matchingItem) {
      this.userInput = matchingItem[this.optionTextField];
      this.valueModel = this.autoCompleteConfig.allowCustomInput ? matchingItem : matchingItem[this.autoCompleteConfig.optionValueField??""];
      this.UpdateFormControl();
      this.bySelect.emit(new TypeaheadMatch(matchingItem));
    } else {
      if (!this.autoCompleteConfig.allowCustomInput) {
        this.userInput = "";
        this.valueModel = null;
        this.bySelect.emit(new TypeaheadMatch(null));
      }
      else {
        const trimmed = (this.userInput ?? "").trim();
        this.valueModel = trimmed.length ? trimmed : null;
        if (trimmed.length) {
          const customItem: any = {
            [this.optionTextField]: trimmed,
            ...(this.autoCompleteConfig.optionValueField ? { [this.autoCompleteConfig.optionValueField]: trimmed } : {}),
            isCustom: true
          };
          this.bySelect.emit(new TypeaheadMatch(customItem));
        } else {
          this.bySelect.emit(new TypeaheadMatch(null));
        }
      }

      this.UpdateFormControl();
    }
    this.updateDisplayState();
  }

  clearValue() {
    this.userInput = "";
    this.valueModel = null;
    this.typeaheadLoading = false;
    this.UpdateFormControl();
    this.updateDisplayState();
    this.bySelect.emit(new TypeaheadMatch(null));
    setTimeout(() => this.inputRef?.nativeElement?.focus());
  }

  private UpdateFormControl() {
    if (this.autoCompleteConfig.formCtrl) {
      this.autoCompleteConfig.formCtrl.patchValue(this.valueModel);
    }
  }

  private syncUserInputFromValueModel(value: any = this._valueModel) {
    if (value === null || value === undefined) {
      this.userInput = "";
      this.updateDisplayState();
      return;
    }

    if (typeof value === "string") {
      this.userInput = value;
    } else if (typeof value === "object" && typeof value?.[this.optionTextField] === "string") {
      this.userInput = value[this.optionTextField];
    } else if (typeof value === "object" && typeof value?.text === "string") {
      this.userInput = value.text;
    } else if (this.dataSourceArray && Array.isArray(this.dataSourceArray)) {
      const dataArray = this.dataSourceArray as Array<any>;
      const valueField = this.autoCompleteConfig?.optionValueField ?? "id";
      const matchingItem = dataArray.find(a => a?.[valueField] === value);
      if (matchingItem && typeof matchingItem?.[this.optionTextField] === "string") {
        this.userInput = matchingItem[this.optionTextField];
      }
    }

    this.updateDisplayState();
  }

  onSearchClick() {
    if (this.autoCompleteConfig.textOnly) {
      return;
    }
    if (this.searchClick.observers.length > 0) {    // if search click event is handled in the host/parent component
      this.searchClick.emit(this.userInput);
    } else {
      if (this.autoCompleteConfig.dataSourceUrl) {
        this.common.listingParams.systemPageId = this.autoCompleteConfig.systemPageId??-1;
        this.common.listingParams.getUrl = this.autoCompleteConfig.dataSourceUrl;
        this.common.listingParams.isSearchEnabled = false;
        this.common.listingParams.selectionType = SelectionType.Single;

        const initialState = {
          listingParams: this.common.listingParams,
          autoCompleteConfig: this.autoCompleteConfig
        };

        this.modalService.onHide.subscribe((reason: string) => {
          if (this.modalRef.content && this.modalRef.content.selectedItems && this.modalRef.content.selectedItems[0]) {
            const selectedItem = this.modalRef.content.selectedItems[0];

            this.userInput = selectedItem[this.autoCompleteConfig.optionTextField??""];
            this.valueModel = selectedItem[this.autoCompleteConfig.optionValueField??""];
            this.UpdateFormControl();
            this.updateDisplayState();
            this.bySelect.emit(new TypeaheadMatch(selectedItem));
          }
        });

        this.modalRef = this.modalService.show(AutoCompleteModalComponent, { initialState, class: 'modal-wide' });
      } else {
        this.inputRef?.nativeElement?.focus();
      }
    }
  }

  private updateDisplayState() {
    const shouldShowClearButton = (this.userInput ?? "").trim().length > 0 || (this.valueModel !== null && this.valueModel !== undefined && this.valueModel !== "");
    this.showClearButton = shouldShowClearButton;
    this.showSearchButton = !this.showClearButton && this.autoCompleteConfig.showSearchIcon === true && !this.autoCompleteConfig.textOnly;
  }
}
